import { z } from 'zod';

import {
  Company,
  ModuleSettingsItem,
  User,
  VisibilityComparator,
  VisibilityRule,
  VisibilityRuleType,
  VisibilityRuleValueItem,
} from 'src/constants';
import type { RecipientType } from 'src/features/recipient-selector/RecipientSelector';
import { CustomFieldType } from 'src/store/clients/types';
import { Tab } from 'src/types/crm_details';
import { ensureUnreachable, generateUniqueId } from 'src/utils/common_utils';

export enum VisibilityType {
  VisibleToEveryone = 'visibleToEveryone',
  NoVisibility = 'noVisibility',
  CustomVisibility = 'customVisibility',
}

export type AllVisibilityRules =
  | { visibility: VisibilityType.VisibleToEveryone; visibilityConfig: [] }
  | {
      visibility: VisibilityType.NoVisibility;
      visibilityConfig: [];
    }
  | {
      visibility: VisibilityType.CustomVisibility;
      visibilityConfig: VisibilityRule[];
    };

export enum VisibilityScenario {
  EditApp = 'EditApp',
  AddAnApp = 'AddAnApp',
}

const visibilityRuleValueItemSchema = z.object({
  userId: z.string(),
  companyId: z.string().optional(),
});

export const clientRuleSchema = z.object({
  ruleType: z.literal(VisibilityRuleType.Client),
  ruleKey: z.string(),
  comparator: z.enum([
    VisibilityComparator.IncludesAnyOf,
    VisibilityComparator.DoesntContainAnyOf,
    VisibilityComparator.IncludesAllOf,
  ]),
  values: z.array(z.string()).min(1, { message: 'Select at least one value' }),
  valueItems: z.array(visibilityRuleValueItemSchema).optional(),
});

export const companyRuleSchema = z.object({
  ruleType: z.literal(VisibilityRuleType.Company),
  ruleKey: z.string(),
  comparator: z.enum([
    VisibilityComparator.IncludesAnyOf,
    VisibilityComparator.DoesntContainAnyOf,
    VisibilityComparator.IncludesAllOf,
  ]),
  values: z.array(z.string()).min(1, { message: 'Select at least one value' }),
});

export const customFieldRuleSchema = z.object({
  ruleType: z.literal(VisibilityRuleType.CustomField),
  ruleKey: z.string(),
  comparator: z.enum([
    VisibilityComparator.IncludesAnyOf,
    VisibilityComparator.DoesntContainAnyOf,
    VisibilityComparator.IncludesAllOf,
  ]),
  values: z.array(z.string()).min(1, { message: 'Select at least one value' }),
  customFieldId: z.string(),
  isCompanyCustomField: z.boolean().optional(),
});

type CustomFieldRuleSchema = z.infer<typeof customFieldRuleSchema>;

export type VisibilityRuleSchema = z.infer<typeof rulesSchema>;

const visbileToEveryOneSchema = z.object({
  visibility: z.literal(VisibilityType.VisibleToEveryone),
});

const noVisibilitySchema = z.object({
  visibility: z.literal(VisibilityType.NoVisibility),
});

const customVisibilitySchema = z.object({
  visibility: z.literal(VisibilityType.CustomVisibility),
  visibilityConfig: z.array(
    z.discriminatedUnion('ruleType', [
      clientRuleSchema,
      companyRuleSchema,
      customFieldRuleSchema,
    ]),
  ),
});

export type VisibileToEveryoneSchema = z.infer<typeof visbileToEveryOneSchema>;
export type CustomVisibilitySchema = z.infer<typeof customVisibilitySchema>;
export type NoVisibilitySchema = z.infer<typeof noVisibilitySchema>;

export const rulesSchema = z.discriminatedUnion('visibility', [
  visbileToEveryOneSchema,
  customVisibilitySchema,
  noVisibilitySchema,
]);

export function isCustomFieldUsedInAppVisibility(
  moduleSettings: ModuleSettingsItem[],
  deletedOption: string,
  deletionComparison: (
    rule: VisibilityRule,
    deletedCustomField: string,
  ) => boolean,
): [boolean, string] {
  for (const module of moduleSettings) {
    const { visibilityConfig } = module;
    if (
      visibilityConfig &&
      visibilityConfig.length > 0 &&
      visibilityConfig.some((visibilityRule) =>
        deletionComparison(visibilityRule, deletedOption),
      )
    ) {
      return [true, module.label];
    }
  }
  return [false, ''];
}

export function getVisibilityConfig(
  values: AllVisibilityRules,
  options?: { enableFeatureAppVisibility?: boolean },
): VisibilityRule[] {
  const { visibility } = values;
  switch (visibility) {
    case VisibilityType.VisibleToEveryone:
      return [];
    case VisibilityType.NoVisibility:
      return [{ ruleType: VisibilityRuleType.NoVisibility }];
    case VisibilityType.CustomVisibility:
      return values.visibilityConfig.map((rule) => {
        if (isCustomFieldRule(rule)) {
          return { ...rule, customFieldId: rule.customFieldId };
        }
        // When flag is OFF, strip valueItems so only plain values are saved
        if (
          !options?.enableFeatureAppVisibility &&
          rule.ruleType === VisibilityRuleType.Client
        ) {
          const { valueItems: _, ...ruleWithoutValueItems } = rule;
          return ruleWithoutValueItems;
        }
        return rule;
      });
    default:
      return ensureUnreachable(visibility);
  }
}

/**
 * Converts legacy values (plain userId strings) to ValueItems format
 * by expanding each userId to all their company associations.
 */
export function expandLegacyValuesToValueItems(
  values: string[],
  clients: User[],
): VisibilityRuleValueItem[] {
  const items: VisibilityRuleValueItem[] = [];
  for (const userId of values) {
    const client = clients.find((c) => c.id === userId);
    if (client?.fields.companyIds?.length) {
      for (const companyId of client.fields.companyIds) {
        items.push({ userId, companyId });
      }
    } else {
      items.push({ userId });
    }
  }
  return items;
}

export function isCustomFieldRule(
  rule: AllVisibilityRules['visibilityConfig'][0],
): rule is CustomFieldRuleSchema {
  return (
    rule.ruleType === VisibilityRuleType.CustomField && 'customFieldId' in rule
  );
}

export function isVisibilityType(value: any): value is VisibilityType {
  return Object.values(VisibilityType).includes(value);
}

export function getVisibilityType(moduleVisbilityConfig: VisibilityRule[]) {
  if (moduleVisbilityConfig.length === 0) {
    return VisibilityType.VisibleToEveryone;
  }
  if (
    moduleVisbilityConfig.length === 1 &&
    moduleVisbilityConfig.at(0)?.ruleType === VisibilityRuleType.NoVisibility
  ) {
    return VisibilityType.NoVisibility;
  }
  return VisibilityType.CustomVisibility;
}

export function getClientVisibilityCount(
  clients: User[],
  companies: Company[],
  visibilityConfig: VisibilityRule[],
) {
  let count = 0;

  // Create a map of companies by ID for efficient lookup
  const companiesMap = new Map<string, Company>();
  for (const company of companies) {
    companiesMap.set(company.id, company);
  }

  for (const client of clients) {
    if (hasClientVisibilityIntoModule(visibilityConfig, client, companiesMap)) {
      count += 1;
    }
  }
  return count;
}

// A checked checkbox contributes this single item, so "is checked" / "is not checked"
// reuse the includesAnyOf / doesntContainAnyOf comparators, same as the backend.
export const CHECKBOX_CHECKED_VALUE = 'true';

/**
 * Reads any stored custom field value as the list the rule comparators work on:
 * multi-select as is, select as one item, checkbox as ['true'] when checked and
 * empty otherwise. Null means the value is missing or not usable.
 */
export function customFieldValueAsList(value: unknown): string[] | null {
  if (Array.isArray(value)) return value as string[];
  if (typeof value === 'string') return [value];
  if (typeof value === 'boolean') return value ? [CHECKBOX_CHECKED_VALUE] : [];
  return null;
}

export function hasClientVisibilityIntoModule(
  visibilityConfig: VisibilityRule[],
  client: User,
  companiesMap?: Map<string, Company>,
): boolean {
  if (visibilityConfig.length === 0) {
    return true;
  }

  for (const rule of visibilityConfig) {
    const { ruleType } = rule;
    switch (ruleType) {
      case VisibilityRuleType.Client: {
        const { comparator, values } = rule;
        switch (comparator) {
          case VisibilityComparator.IncludesAnyOf: {
            // New format: use valueItems with explicit userId/companyId
            if (rule.valueItems && rule.valueItems.length > 0) {
              const clientCompanyIds =
                client.fields.companyIds && client.fields.companyIds.length > 0
                  ? client.fields.companyIds
                  : [client.fields.companyId || ''];
              for (const item of rule.valueItems) {
                if (item.userId !== client.id) {
                  continue;
                }
                if (!item.companyId) {
                  // No company specified — matches all company contexts
                  return true;
                }
                if (clientCompanyIds.includes(item.companyId)) {
                  return true;
                }
              }
            } else {
              // Legacy format: plain userId strings in values
              if (values.includes(client.id)) {
                return true;
              }
            }
            break;
          }
          case VisibilityComparator.DoesntContainAnyOf:
          case VisibilityComparator.IncludesAllOf:
            continue;
          default:
            ensureUnreachable(comparator);
        }
        continue;
      }
      case VisibilityRuleType.Company: {
        const { comparator, values } = rule;
        switch (comparator) {
          case VisibilityComparator.IncludesAnyOf:
            if (
              // TODO:(enable_many_companies) remove the companyId check once we enable many companies
              values.includes(client.fields.companyId || '') ||
              client.fields.companyIds?.some((id) => values.includes(id))
            ) {
              return true;
            }
            break;
          case VisibilityComparator.DoesntContainAnyOf:
          case VisibilityComparator.IncludesAllOf:
            continue;
          default:
            ensureUnreachable(comparator);
        }
        continue;
      }
      case VisibilityRuleType.CustomField: {
        const { comparator, values, customFieldId, isCompanyCustomField } =
          rule;

        // Handle company custom fields
        if (isCompanyCustomField) {
          if (!companiesMap) {
            // No companies map provided, skip this rule
            continue;
          }

          // Get client's company IDs
          const clientCompanyIds: string[] = client.fields.companyIds || [];

          // The rule passes when any of the client's companies satisfies it, like the
          // backend; a company without a value counts as an empty list.
          const companyValueLists = clientCompanyIds
            .map((cId) => companiesMap.get(cId))
            .filter((company): company is Company => !!company)
            .map((company) =>
              customFieldValueAsList(
                company.fields.customFields?.[customFieldId],
              ),
            );
          if (companyValueLists.length === 0) {
            companyValueLists.push(null);
          }

          for (const companyValues of companyValueLists) {
            const customFieldIds = new Set(companyValues ?? []);
            switch (comparator) {
              case VisibilityComparator.IncludesAnyOf:
                if (values.some((value) => customFieldIds.has(value))) {
                  return true;
                }
                break;
              case VisibilityComparator.IncludesAllOf:
                if (
                  companyValues &&
                  values.every((value) => customFieldIds.has(value))
                ) {
                  return true;
                }
                break;
              case VisibilityComparator.DoesntContainAnyOf:
                if (!values.some((value) => customFieldIds.has(value))) {
                  return true;
                }
                break;
              default:
                ensureUnreachable(comparator);
            }
          }
        } else {
          // Client custom fields (existing logic)
          switch (comparator) {
            case VisibilityComparator.IncludesAnyOf: {
              const customFieldValue = new Set(
                customFieldValueAsList(
                  client.fields.customFields?.[customFieldId],
                ) ?? [],
              );
              if (values.some((value) => customFieldValue.has(value))) {
                return true;
              }
              break;
            }
            case VisibilityComparator.DoesntContainAnyOf: {
              const customFieldValue = new Set(
                customFieldValueAsList(
                  client.fields.customFields?.[customFieldId],
                ) ?? [],
              );
              if (!values.some((value) => customFieldValue.has(value))) {
                return true;
              }
              break;
            }
            case VisibilityComparator.IncludesAllOf:
              {
                const customFieldValue = new Set(
                  customFieldValueAsList(
                    client.fields.customFields?.[customFieldId],
                  ) ?? [],
                );
                if (values.every((value) => customFieldValue.has(value))) {
                  return true;
                }
              }
              continue;
            default:
              ensureUnreachable(comparator);
          }
        }
        continue;
      }
      case VisibilityRuleType.NoVisibility: {
        continue;
      }

      default:
        ensureUnreachable(ruleType);
    }
  }

  return false;
}

export type ComparatorField = {
  id: string;
  label: string;
  type: VisibilityRuleType;
  isCompanyCustomField: boolean;
};

export type VisibilityRecipient = {
  clientId: string;
  companyId: string;
  type: RecipientType;
};

type ComparatorRules = {
  id: VisibilityComparator;
  singularLabel: string;
  pluralLabel: string;
};

const COMPARATOR_RULES_PER_VISIBILITY_RULE: Record<
  VisibilityRuleType,
  ComparatorRules[]
> = {
  [VisibilityRuleType.Client]: [
    {
      id: VisibilityComparator.IncludesAnyOf,
      singularLabel: 'Is',
      pluralLabel: 'Is any of',
    },
  ],
  [VisibilityRuleType.Company]: [
    {
      id: VisibilityComparator.IncludesAnyOf,
      singularLabel: 'Is',
      pluralLabel: 'Is any of',
    },
  ],
  [VisibilityRuleType.CustomField]: [
    {
      id: VisibilityComparator.IncludesAnyOf,
      singularLabel: 'Includes',
      pluralLabel: 'Includes any of',
    },
    {
      id: VisibilityComparator.IncludesAllOf,
      singularLabel: 'Includes',
      pluralLabel: 'Includes all of',
    },
    {
      id: VisibilityComparator.DoesntContainAnyOf,
      singularLabel: 'Does not include',
      pluralLabel: "Doesn't include any of",
    },
  ],
  [VisibilityRuleType.NoVisibility]: [],
};

const CHECKBOX_COMPARATOR_RULES: ComparatorRules[] = [
  {
    id: VisibilityComparator.IncludesAnyOf,
    singularLabel: 'Is checked',
    pluralLabel: 'Is checked',
  },
  {
    id: VisibilityComparator.DoesntContainAnyOf,
    singularLabel: 'Is not checked',
    pluralLabel: 'Is not checked',
  },
];

export function getComparatorRules(
  ruleType: VisibilityRuleType,
  selectedValues: string[] = [],
  fieldType?: CustomFieldType,
): ComparatorRules[] {
  if (ruleType === VisibilityRuleType.CustomField && fieldType === 'checkbox') {
    return CHECKBOX_COMPARATOR_RULES;
  }
  const rules = COMPARATOR_RULES_PER_VISIBILITY_RULE[ruleType];
  // A select holds one value, so "includes all of" can never differ from "any of".
  if (
    ruleType === VisibilityRuleType.CustomField &&
    (selectedValues.length <= 1 || fieldType === 'select')
  ) {
    return rules.filter((r) => r.id !== VisibilityComparator.IncludesAllOf);
  }
  return rules;
}

export function getDefaultVisibilityRule(): VisibilityRule {
  return {
    ruleType: VisibilityRuleType.Client,
    comparator: VisibilityComparator.IncludesAnyOf,
    values: [],
    valueItems: [],
    ruleKey: generateUniqueId(),
  };
}

export function applyVisibilityTypeChange(
  newType: VisibilityType,
  setFieldValue: (field: string, value: unknown) => void,
): void {
  switch (newType) {
    case VisibilityType.VisibleToEveryone:
      void setFieldValue('visibilityConfig', []);
      break;
    case VisibilityType.NoVisibility:
      void setFieldValue('visibilityConfig', [
        { ruleType: VisibilityRuleType.NoVisibility },
      ]);
      break;
    case VisibilityType.CustomVisibility:
      void setFieldValue('visibilityConfig', [getDefaultVisibilityRule()]);
      break;
    default:
      ensureUnreachable(newType);
  }
}

export function isTabVisibleToClient(
  tab: Tab<string>,
  activeClient: User,
  visibilityConfigMapping: Record<string, VisibilityRule[]>,
) {
  switch (tab.key) {
    case 'contracts':
      return hasClientVisibilityIntoModule(
        visibilityConfigMapping['contracts'],
        activeClient,
      );
    case 'files':
      return hasClientVisibilityIntoModule(
        visibilityConfigMapping['files'],
        activeClient,
      );
    case 'forms':
      return hasClientVisibilityIntoModule(
        visibilityConfigMapping['forms'],
        activeClient,
      );
    case 'messages':
      return hasClientVisibilityIntoModule(
        visibilityConfigMapping['messages'],
        activeClient,
      );
    case 'invoices':
      return hasClientVisibilityIntoModule(
        visibilityConfigMapping['payments'],
        activeClient,
      );
    default:
      if (z.string().uuid().safeParse(tab.key).success) {
        return hasClientVisibilityIntoModule(
          visibilityConfigMapping[tab.key],
          activeClient,
        );
      } else {
        return false;
      }
  }
}
